import React from 'react';
import { ArrowLeft, AlertTriangle, Brain, Lightbulb, Heart } from 'lucide-react';
import { RadialBarChart, RadialBar, ResponsiveContainer } from 'recharts';
import { useAssessmentStore } from '../../store';

interface BrsResultsProps {
  scores: {
    score: number;
    severity: string;
    color: string;
  };
  onBack: () => void;
}

const BrsResults: React.FC<BrsResultsProps> = ({ scores, onBack }) => {
  const { setBrsResults, setAssessmentComplete } = useAssessmentStore();

  React.useEffect(() => {
    setBrsResults(scores);
    setAssessmentComplete('brs');
  }, [scores]);

  const data = [
    {
      name: '心理韌性',
      value: scores.score * 20, // Convert 1-5 scale to percentage
      fill: scores.color.includes('red') ? '#ef4444' : 
            scores.color.includes('green') ? '#22c55e' : '#3b82f6'
    }
  ];

  return (
    <div className="flex flex-col h-full">
      <div className="bg-white rounded-xl shadow-lg p-6 flex-1 overflow-y-auto">
        <div className="flex items-center mb-6">
          <button
            onClick={onBack}
            className="mr-4 p-2 hover:bg-gray-100 rounded-lg"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <h2 className="text-2xl font-bold text-gray-800">心理韌性評估結果</h2>
        </div>

        <div className="space-y-6">
          {/* Main Score with Gauge Chart */}
          <div className="bg-white rounded-lg border border-gray-200 p-6 text-center">
            <h3 className="text-lg font-semibold text-gray-700 mb-3">您的心理韌性分數</h3>
            <div className="h-48 mb-4">
              <ResponsiveContainer width="100%" height="100%">
                <RadialBarChart
                  cx="50%"
                  cy="50%"
                  innerRadius="60%"
                  outerRadius="100%"
                  barSize={10}
                  data={data}
                  startAngle={180}
                  endAngle={0}
                >
                  <RadialBar
                    background
                    clockWise
                    dataKey="value"
                    cornerRadius={10}
                  />
                </RadialBarChart>
              </ResponsiveContainer>
            </div>
            <div className="text-4xl font-bold mb-3">
              {scores.score.toFixed(2)}
            </div>
            <div className={`inline-block px-4 py-2 rounded-full ${scores.color}`}>
              {scores.severity}
            </div>
          </div>

          {/* Score Scale */}
          <div className="bg-gray-50 rounded-lg p-6">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">分數解讀</h3>
            <div className="space-y-3">
              <div className="flex items-center">
                <div className="w-24 text-sm text-gray-500">1.00-2.99</div>
                <div className="bg-red-100 text-red-800 px-3 py-1 rounded-full text-sm">
                  心理韌性較低
                </div>
              </div>
              <div className="flex items-center">
                <div className="w-24 text-sm text-gray-500">3.00-4.30</div>
                <div className="bg-green-100 text-green-800 px-3 py-1 rounded-full text-sm">
                  正常心理韌性
                </div>
              </div>
              <div className="flex items-center">
                <div className="w-24 text-sm text-gray-500">4.31-5.00</div>
                <div className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm">
                  高心理韌性
                </div>
              </div>
            </div>
          </div>

          {/* Recommendations */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-gray-800">建議</h3>
            
            <div className="grid gap-4 md:grid-cols-3">
              <div className="bg-purple-50 rounded-lg p-4">
                <div className="flex items-center mb-3">
                  <Brain className="w-5 h-5 text-purple-500 mr-2" />
                  <h4 className="font-medium text-purple-900">認知策略</h4>
                </div>
                <ul className="text-sm text-purple-800 space-y-2">
                  <li>• 培養積極思維模式</li>
                  <li>• 學習問題解決技巧</li>
                  <li>• 設定實際可行的目標</li>
                </ul>
              </div>

              <div className="bg-blue-50 rounded-lg p-4">
                <div className="flex items-center mb-3">
                  <Heart className="w-5 h-5 text-blue-500 mr-2" />
                  <h4 className="font-medium text-blue-900">情緒管理</h4>
                </div>
                <ul className="text-sm text-blue-800 space-y-2">
                  <li>• 練習正念冥想</li>
                  <li>• 保持規律運動習慣</li>
                  <li>• 建立支持系統網絡</li>
                </ul>
              </div>

              <div className="bg-green-50 rounded-lg p-4">
                <div className="flex items-center mb-3">
                  <Lightbulb className="w-5 h-5 text-green-500 mr-2" />
                  <h4 className="font-medium text-green-900">生活調適</h4>
                </div>
                <ul className="text-sm text-green-800 space-y-2">
                  <li>• 維持健康作息</li>
                  <li>• 培養興趣愛好</li>
                  <li>• 學習壓力管理技巧</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Disclaimer */}
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <div className="flex items-start">
              <AlertTriangle className="w-5 h-5 text-yellow-500 mt-0.5 mr-2 flex-shrink-0" />
              <div className="text-sm text-yellow-700">
                <p className="font-medium mb-1">重要提醒</p>
                <p>此評估工具僅供參考，不能作為臨床診斷的依據。如果你正在經歷情緒困擾，建議尋求專業心理諮詢師或醫生的協助。</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BrsResults;